import Layout from "@/components/layout/Layout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { SEO } from "@/components/SEO";

const Solutions = () => {
  const { data: solutions, isLoading } = useQuery({
    queryKey: ['solutions-page'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('partners' as any)
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  return (
    <Layout>
      <SEO 
        title="פתרונות למערכות" 
        description="כלים, שירותים ומומחים שיעזרו לכם לשדרג, לחבר ולהפיץ את המערכת שבניתם." 
      />
      <div className="bg-[#fbfbfd] min-h-screen pb-20">
        
        {/* Header - Minimalist Apple Style */}
        <div className="pt-32 pb-24 border-b border-slate-200/60 bg-white">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <h1 className="text-5xl lg:text-7xl font-semibold text-[#1d1d1f] tracking-tight mb-6 leading-tight">
              פתרונות למערכות.
            </h1>
            <p className="text-xl lg:text-2xl text-[#86868b] leading-relaxed font-normal max-w-2xl mx-auto mb-10">
              כלים, שירותים ומומחים שיעזרו לכם לשדרג, לחבר ולהפיץ את המערכת שבניתם.
            </p>

            <div className="inline-block border-t border-slate-100 pt-8 px-4">
              <p className="text-base text-slate-900 font-medium mb-1">
                יש לכם פתרון שמוסיף ערך למערכות דיגיטליות חכמות?
              </p>
              <p className="text-sm text-slate-500">
                אנחנו בוחנים שיתופי פעולה עם גורמים מקצועיים.{" "}
                <Link to="/contact" className="text-blue-600 hover:text-blue-800 font-semibold hover:underline inline-flex items-center gap-1 transition-colors">
                  דברו איתנו
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="container mx-auto px-6 max-w-7xl py-24">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-3xl p-8 h-[400px] border border-slate-100 shadow-sm flex flex-col gap-6">
                  <Skeleton className="h-20 w-20 rounded-2xl" />
                  <div className="space-y-3 mt-auto">
                    <Skeleton className="h-8 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : solutions?.length === 0 ? (
            <div className="text-center py-32">
              <p className="text-[#86868b] text-xl font-medium">טרם פורסמו פתרונות.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {solutions?.map((solution: any) => (
                <Link 
                  key={solution.id} 
                  to={`/partners/${solution.slug}`}
                  className="group bg-white rounded-[2rem] p-10 shadow-[0_2px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all duration-300 flex flex-col h-full border border-slate-100 hover:border-slate-200/60 relative overflow-hidden"
                >
                  <div className="w-20 h-20 bg-[#f5f5f7] rounded-2xl flex items-center justify-center p-4 mb-8 transition-transform duration-500">
                    <img 
                      src={solution.logo_url} 
                      alt={solution.name} 
                      className="w-full h-full object-contain mix-blend-multiply"
                    />
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <h3 className="text-2xl font-bold text-[#1d1d1f] flex items-center gap-2">
                      {solution.name}
                      {/* Apple-style dark verification badge */}
                      <CheckCircle2 className="w-5 h-5 text-[#1d1d1f] fill-[#1d1d1f]/10" strokeWidth={2.5} />
                    </h3>
                    <p className="text-[#86868b] leading-relaxed text-base font-medium">
                      {solution.short_desc}
                    </p>
                  </div>

                  <div className="mt-auto pt-8 flex items-center text-[#0066cc] font-semibold text-lg group-hover:underline decoration-2 underline-offset-4 transition-all">
                    לפרטים נוספים
                    <ArrowLeft className="w-5 h-5 mr-2 transition-transform duration-300 group-hover:-translate-x-1" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
};

export default Solutions;